import React, { useEffect, useState, useCallback } from 'react';
import './PriceEditor.css';

// Test and prod prices side by side. Both are written straight to their table
// (not through the test→prod sync, which never copies the price). Stored in
// kopecks, edited in rubles.

const toRubles = (kopecks) => (kopecks == null ? '' : String(kopecks / 100));
const fmtRub = (kopecks) => (kopecks == null ? '—' : `${(kopecks / 100).toLocaleString('ru-RU')} ₽`);

// "1 400", "1400,50" → kopecks; null when the input is not a price.
const parseRubles = (s) => {
  const n = Number(String(s).replace(/\s/g, '').replace(',', '.'));
  if (String(s).trim() === '' || !Number.isFinite(n) || n < 0) return null;
  return Math.round(n * 100);
};

// Keep the whole server message: a bare "HTTP 500" explains nothing.
const errorText = async (r) => {
  const body = await r.text();
  try {
    return JSON.parse(body).error || body;
  } catch {
    return `HTTP ${r.status}: ${body.slice(-2000)}`;
  }
};

const PriceField = ({ target, row, canEdit, onSave }) => {
  const rowPrice = row ? row.price : null;
  const rowId = row ? row.quest_id : null;
  const [value, setValue] = useState(toRubles(rowPrice));
  const [status, setStatus] = useState('idle'); // idle | saving | saved | error
  const [error, setError] = useState(null);

  useEffect(() => { setValue(toRubles(rowPrice)); }, [rowPrice]);
  useEffect(() => { setStatus('idle'); setError(null); }, [rowId]);

  if (!row) {
    return (
      <div className="price-field">
        <span className="price-field__label">{target === 'prod' ? 'Prod' : 'Test'}</span>
        <span className="price-field__missing">not in {target}</span>
      </div>
    );
  }

  const next = parseRubles(value);
  const dirty = next !== null && next !== row.price;

  const save = async () => {
    if (!dirty) return;
    if (target === 'prod' && !window.confirm(
      `Prod price for ${row.quest_id}: ${fmtRub(row.price)} → ${fmtRub(next)}\n\n` +
      'App checkout changes immediately, istanbul.quest within an hour. ' +
      'Sputnik and Tripster are not affected.\n\nSave?'
    )) return;
    setStatus('saving');
    setError(null);
    try {
      await onSave(target, next, row.price);
      setStatus('saved');
    } catch (e) {
      setStatus('error');
      setError(e.message);
    }
  };

  return (
    <div className="price-field">
      <span className="price-field__label">
        {target === 'prod' ? 'Prod' : 'Test'}
        <span className="price-field__qid">{row.quest_id}</span>
      </span>
      <div className="price-field__row">
        <input
          className="price-field__input"
          inputMode="decimal"
          value={value}
          disabled={!canEdit || status === 'saving'}
          onChange={(e) => { setValue(e.target.value); setStatus('idle'); }}
          onKeyDown={(e) => { if (e.key === 'Enter') save(); }}
        />
        <span className="price-field__unit">₽</span>
        <button
          className={`save-button price-field__save ${status}`}
          disabled={!canEdit || !dirty || status === 'saving'}
          onClick={save}
        >
          {status === 'saving' ? 'Saving…' : status === 'saved' && !dirty ? 'Saved ✓' : 'Save'}
        </button>
      </div>
      {value !== '' && next === null && (
        <span className="price-field__error">Not a price</span>
      )}
      {error && <span className="price-field__error">{error}</span>}
      {!canEdit && (
        <span className="price-field__hint">
          {target === 'prod' ? 'Needs push-to-prod permission' : 'No access to the test quest'}
        </span>
      )}
      {target === 'prod' && canEdit && (
        <span className="price-field__hint">
          App — immediately, istanbul.quest — within an hour, Sputnik/Tripster — not affected
        </span>
      )}
    </div>
  );
};

const PriceEditor = ({ questId, selectedTable, onPreviewChange }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const table = selectedTable || 'quests_test';

  const load = useCallback(async () => {
    const r = await fetch(`/api/quest-price/${encodeURIComponent(questId)}?table=${table}`, {
      credentials: 'include',
    });
    if (!r.ok) throw new Error(await errorText(r));
    return r.json();
  }, [questId, table]);

  // Saved test/prod rows for the app preview.
  useEffect(() => {
    if (onPreviewChange) onPreviewChange(data);
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!questId) return undefined;
    let cancelled = false;
    setData(null);
    setError(null);
    load()
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => { if (!cancelled) setError(e.message); });
    return () => { cancelled = true; };
  }, [questId, load]);

  const save = async (target, price, expected) => {
    const r = await fetch(`/api/quest-price/${encodeURIComponent(questId)}?table=${table}`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ target, price, expected }),
    });
    if (!r.ok) {
      const msg = await errorText(r);
      // Someone changed it meanwhile: show the fresh value alongside the error.
      if (r.status === 409) load().then(setData).catch(() => {});
      throw new Error(msg);
    }
    const saved = await r.json();
    setData((d) => ({ ...d, [target]: { ...d[target], price: saved.price } }));
  };

  return (
    <div className="price-editor">
      <div className="price-editor__title">💰 Price</div>
      {error && <div className="price-field__error">{error}</div>}
      {!data && !error && <div className="price-editor__loading">Loading…</div>}
      {data && (
        <div className="price-editor__grid">
          <PriceField target="test" row={data.test} canEdit={data.canEditTest} onSave={save} />
          <PriceField target="prod" row={data.prod} canEdit={data.canEditProd} onSave={save} />
        </div>
      )}
    </div>
  );
};

export default PriceEditor;
